import { useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";
import { COUNTRY_CODES } from "common/constants/countries";
import { getCountryName } from "common/helpers/utils";

interface CountrySelectProps {
  selectedCountry: string | undefined;
  onSelect: (countryCode: string) => void;
  disabled?: boolean;
}

const countries = COUNTRY_CODES.map((code) => ({
  code,
  name: getCountryName(code),
}));

// Auto-focuses the search input when the dropdown mounts
const focusInput = (node: HTMLInputElement | null) => {
  if (node) requestAnimationFrame(() => node.focus());
};

export default function CountrySelect({
  selectedCountry,
  onSelect,
  disabled = false,
}: CountrySelectProps) {
  const [search, setSearch] = useState("");

  const q = search.toLowerCase();
  const filtered = countries.filter(({ name }) =>
    name?.toLowerCase().includes(q),
  );

  const selectedName = selectedCountry ? getCountryName(selectedCountry) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <SelectButton hasSelection={!!selectedCountry}>
          {selectedCountry ? (
            <span className="flex items-center gap-2">
              <ReactCountryFlag countryCode={selectedCountry} />
              {selectedName}
            </span>
          ) : (
            <span className="text-black-500">Select country</span>
          )}
        </SelectButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
        <MenuSearchInput
          ref={focusInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <DropdownMenuList>
          {filtered.length ? (
            <div className="max-h-48 overflow-y-auto scrollbar-thin">
              {filtered.map(({ code, name }) => (
                <DropdownMenuItem key={code} onSelect={() => onSelect(code)}>
                  <ReactCountryFlag countryCode={code} className="mr-2" />
                  <span className="truncate">{name}</span>
                </DropdownMenuItem>
              ))}
            </div>
          ) : (
            <p className="mx-4 break-words py-4 text-center text-black-500">
              No results for <span className="text-black-950">{search}</span>
            </p>
          )}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
